import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SupplierAttachmentsMessage } from '@1po/1po-bff-fe-spec/generated/claim/response/model/SupplierAttachmentsMessage';
import { SupplierComment } from '@1po/1po-bff-fe-spec/generated/claim/response/model/SupplierComment';
import { useTheme } from 'styled-components';
import { ArrowRightFullIcon, BackIcon } from 'assets/icons';
import {
  fetchClaimDocumentDownloadUrlRequestSaga,
  getOrderClaimDownloadingAttachmentIds,
} from 'domains/order/Order.store';
import {
  OriginalContent,
  SAntCollapse,
  SAttachmentContent,
  SCommentContent,
  SContainer,
  SFooterButton,
  SLabelContainer,
  STimeline,
} from 'pages/MyOrdersPage/OrderClaims/Drawer/ClaimDrawer.styled';
import {
  ALL_DRAWER,
  ClaimDrawerProps,
  COMMENT,
  DOCUMENTS_DRAWER,
  SupplierAttachmentType,
  TimelineItem,
} from 'pages/MyOrdersPage/OrderClaims/Drawer/ClaimDrawer.types';
import { buildTimelineItems, formatDateTime } from 'pages/MyOrdersPage/OrderClaims/Drawer/ClaimDrawer.utils';
import { Flex, Icon, Spin, Text } from 'UI';
import { SButton } from 'UI/Button/BackButton.styled';
import { Drawer } from 'UI/Drawer';

export const ClaimDrawer = ({ claim, disclosure, timelineType, onTimelineTypeChange }: ClaimDrawerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const downloadingAttachmentIds = useSelector(getOrderClaimDownloadingAttachmentIds);

  const downloadAttachment = React.useCallback(
    (fileName: string, fileKey: string, fileUrl?: string) => {
      if (!downloadingAttachmentIds.has(fileKey)) {
        dispatch(fetchClaimDocumentDownloadUrlRequestSaga({ fileName, fileKey, fileUrl }));
      }
    },
    [dispatch, downloadingAttachmentIds],
  );

  const renderAttachments = React.useCallback(
    (supplierAttachments: SupplierAttachmentType[] | undefined) => {
      if (!supplierAttachments || supplierAttachments.length === 0) {
        return (
          <Text type="base" color="grey35">
            {t('order.claim.timeline.no_attachments', 'No attachments')}
          </Text>
        );
      }

      return (
        <SContainer>
          <SAttachmentContent>
            {supplierAttachments.map((attachment) => {
              const isDownloading = downloadingAttachmentIds.has(attachment.attachmentId);
              return (
                <div key={attachment.attachmentId}>
                  <Flex gap={8} align={'center'}>
                    <Text
                      displayStyle={'link'}
                      decoration={'underline'}
                      hoverUnderLine
                      cursor={'pointer'}
                      type="base"
                      color="grey55"
                      onClick={() =>
                        downloadAttachment(attachment.attachmentName, attachment.attachmentId, attachment.attachmentUrl)
                      }
                    >
                      {attachment.attachmentName}
                    </Text>
                    {isDownloading && <Spin size={'small'} />}
                  </Flex>
                </div>
              );
            })}
          </SAttachmentContent>
        </SContainer>
      );
    },
    [t, downloadAttachment, downloadingAttachmentIds],
  );

  const renderTimelineItem = React.useCallback(
    (item: TimelineItem) => {
      if (item.type === COMMENT) {
        const supplierComment = item.data as SupplierComment;
        return {
          children: (
            <>
              <Text type="base" color="grey35">
                {formatDateTime(supplierComment.commentCreationDateTime)}
              </Text>
              <SAntCollapse
                ghost
                expandIconPosition="end"
                items={[
                  {
                    key: supplierComment.commentId,
                    label: (
                      <SLabelContainer maxWidth={250}>
                        <Text type="base">
                          {`${t('order.claim.timeline.new_message', '[New Message]')} ${supplierComment.comment}`}
                        </Text>
                      </SLabelContainer>
                    ),
                    children: (
                      <SContainer>
                        <SCommentContent>
                          <Text type="base">{supplierComment.comment}</Text>
                        </SCommentContent>
                      </SContainer>
                    ),
                  },
                ]}
              />
            </>
          ),
        };
      } else {
        const supplierAttachmentMessage = item.data as SupplierAttachmentsMessage;
        return {
          children: (
            <>
              <Text type="base" color="grey35">
                {formatDateTime(supplierAttachmentMessage.attachmentsCreationDateTime)}
              </Text>
              <SAntCollapse
                ghost
                expandIconPosition="end"
                items={[
                  {
                    key: supplierAttachmentMessage.attachmentsId,
                    label: (
                      <SLabelContainer maxWidth={250}>
                        <Text type="base">{t('order.claim.timeline.new_files_added', '[New Files added]')}</Text>
                      </SLabelContainer>
                    ),
                    children: renderAttachments(supplierAttachmentMessage.attachments as SupplierAttachmentType[]),
                  },
                ]}
              />
            </>
          ),
        };
      }
    },
    [t, renderAttachments],
  );

  const timelineItems = React.useMemo(() => {
    const items = buildTimelineItems(claim, timelineType);
    return items.map(renderTimelineItem);
  }, [claim, timelineType, renderTimelineItem]);

  const isAllDrawerMode = timelineType === ALL_DRAWER;
  const showMessage = isAllDrawerMode && !!claim?.comment;
  const showAttachments = !!claim?.garageAttachments?.length;
  const showInitialContent = showMessage || showAttachments;

  return (
    <Drawer
      title={t('order.claim.timeline.comments.title', 'Claim #{{claimId}} Timeline', {
        claimId: claim?.externalClaimId,
      })}
      footer={
        <Flex direction={'row'} justify={isAllDrawerMode ? 'flex-end' : 'flex-start'}>
          {isAllDrawerMode ? (
            <SFooterButton onClick={() => onTimelineTypeChange(DOCUMENTS_DRAWER)}>
              <SButton gap={5} align={'center'}>
                <Text type={'light_14'} displayStyle={'link'} decoration={'underline'} hoverUnderLine={true}>
                  {t('order.claim.timeline.see_documents', 'See all attached documents')}
                </Text>
                <Icon IconComponent={ArrowRightFullIcon} size={16} mb={2} color={theme.color.blue} />
              </SButton>
            </SFooterButton>
          ) : (
            <SFooterButton onClick={() => onTimelineTypeChange(ALL_DRAWER)}>
              <SButton gap={5} align={'center'}>
                <Icon IconComponent={BackIcon} size={16} mb={2} />
                <Text type={'light_14'} decoration={'underline'} hoverUnderLine={true} noWrap>
                  {t('common.action.back', 'Back')}
                </Text>
              </SButton>
            </SFooterButton>
          )}
        </Flex>
      }
      disclosure={disclosure}
    >
      {timelineItems.length > 0 && <STimeline items={timelineItems} />}
      {timelineItems.length === 0 && !showInitialContent && (
        <Text type="base" color="grey35">
          {t('order.claim.timeline.no_timeline_items', 'No timeline items available')}
        </Text>
      )}
      {showInitialContent && (
        <OriginalContent direction={'column'} gap={10}>
          {showMessage && (
            <Flex direction={'column'}>
              <Text type="base" bold>
                {t('order.claim.timeline.your_original_message', 'Your original message')}
              </Text>
              <Text type="base" color="grey55">
                {claim?.comment}
              </Text>
            </Flex>
          )}
          {showAttachments && (
            <Flex direction={'column'}>
              <Text type="base" bold>
                {t('order.claim.timeline.uploaded_documents', 'Uploaded documents')}
              </Text>
              {(claim.garageAttachments || []).map((attachment) => {
                const isDownloading = downloadingAttachmentIds.has(attachment.key || '');
                return (
                  <Flex key={attachment.key} gap={8} align={'center'}>
                    <Text
                      displayStyle={'link'}
                      decoration={'underline'}
                      hoverUnderLine
                      cursor={'pointer'}
                      type="base"
                      color="grey55"
                      onClick={() => downloadAttachment(attachment.fileName || '', attachment.key || '')}
                    >
                      {attachment.fileName}
                    </Text>
                    {isDownloading && <Spin size={'small'} />}
                  </Flex>
                );
              })}
            </Flex>
          )}
        </OriginalContent>
      )}
    </Drawer>
  );
};
